import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useState, useEffect, createContext, useContext, useCallback, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import axios from "axios";

// Eager: Login is the entry point
import LoginPage from "@/pages/LoginPage";
import { logger } from "@/lib/logger";
import RandomLoader from "@/components/RandomLoader";

// Lazy-loaded pages (code splitting)
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const ScreenerPage = lazy(() => import("@/pages/ScreenerPage"));
const CompanyPage = lazy(() => import("@/pages/CompanyPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const StrategicAnalysisPage = lazy(() => import("@/pages/StrategicAnalysisPage"));
const DataRoomPage = lazy(() => import("@/pages/DataRoomPage"));
const DataRoomLoginPage = lazy(() => import("@/pages/DataRoomLoginPage"));
const DataRoomExternalView = lazy(() => import("@/pages/DataRoomExternalView"));
const DataRoomResetPasswordPage = lazy(() => import("@/pages/DataRoomResetPasswordPage"));
const DataRoomSelectorPage = lazy(() => import("@/pages/DataRoomSelectorPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const BillingPage = lazy(() => import("@/pages/BillingPage"));
const PortalLoginPage = lazy(() => import("@/pages/portal/PortalLoginPage"));
const PortalDashboardPage = lazy(() => import("@/pages/portal/PortalDashboardPage"));
const PortalStrategicPage = lazy(() => import("@/pages/portal/PortalStrategicPage"));
const PortalCompanyPage = lazy(() => import("@/pages/portal/PortalCompanyPage"));
const SearchResultsPage = lazy(() => import("@/pages/SearchResultsPage"));
const PortalOOHMapPage = lazy(() => import("@/pages/portal/PortalOOHMapPage"));
const PortalResetPasswordPage = lazy(() => import("@/pages/portal/PortalResetPasswordPage"));
const MARadarPage = lazy(() => import("@/pages/MARadarPage"));
const ChangePasswordPage = lazy(() => import("@/pages/ChangePasswordPage"));
const LegalNoticePage = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.LegalNoticePage })));
const PrivacyPolicyPage = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.PrivacyPolicyPage })));
const CookiesPolicyPage = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.CookiesPolicyPage })));
const TermsSaaSPage = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.TermsSaaSPage })));
const BillingTermsPage = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.BillingTermsPage })));
const AcceptableUsePage = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.AcceptableUsePage })));
const FinancialDisclaimerPage = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.FinancialDisclaimerPage })));
const SecurityPage = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.SecurityPage })));
const StatusPage = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.StatusPage })));
const ContactPage = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.ContactPage })));
const LandingPage = lazy(() => import("@/pages/LandingPage"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Set global axios auth header from localStorage
  useEffect(() => {
    const token = sessionStorage.getItem("session_token");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
  }, []);

  const checkAuth = useCallback(async () => {
    const token = sessionStorage.getItem("session_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch {
      sessionStorage.removeItem("session_token");
      delete axios.defaults.headers.common["Authorization"];
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (userData, sessionToken) => {
    sessionStorage.setItem("session_token", sessionToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${sessionToken}`;
    // Only set flag if explicitly true from login response
    if (userData.must_change_password === true) {
      sessionStorage.setItem("must_change_password", "true");
    } else {
      sessionStorage.removeItem("must_change_password");
    }
    setUser(userData);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      logger.error("Logout error", e);
    }
    sessionStorage.removeItem("session_token");
    sessionStorage.removeItem("must_change_password");
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

import { SubscriptionProvider, SubscriptionGuard } from "@/components/SubscriptionGuard";

function LandingOrDashboard() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><RandomLoader size={64} /></div>;
  if (user) return <Navigate to="/dashboard" replace />;
  return <LandingPage />;
}

// Protected Route
function ProtectedRoute({ children, skipSubGuard = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 flex items-center justify-center"><RandomLoader size={64} /></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Force password change if required
  if (sessionStorage.getItem("must_change_password") === "true" && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }

  if (skipSubGuard) return children;

  return (
    <SubscriptionProvider userEmail={user.email}>
      <SubscriptionGuard>{children}</SubscriptionGuard>
    </SubscriptionProvider>
  );
}

// App Router
function AppRouter() {
  return (
    <Suspense fallback={<div className="flex items-center justify-center h-screen"><RandomLoader size={48} /></div>}>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/screener"
        element={
          <ProtectedRoute>
            <ScreenerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/company/:companyId"
        element={
          <ProtectedRoute>
            <CompanyPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/strategic"
        element={
          <ProtectedRoute>
            <StrategicAnalysisPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ma-radar"
        element={
          <ProtectedRoute>
            <MARadarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/search/results"
        element={
          <ProtectedRoute>
            <SearchResultsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/change-password"
        element={
          <ProtectedRoute skipSubGuard>
            <ChangePasswordPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute skipSubGuard>
            <BillingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing/success"
        element={
          <ProtectedRoute skipSubGuard>
            <BillingPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/dataroom/:dealId"
        element={
          <ProtectedRoute>
            <DataRoomPage />
          </ProtectedRoute>
        }
      />
      {/* External Data Room routes - no admin auth required */}
      <Route path="/vdr" element={<DataRoomLoginPage />} />
      <Route path="/dataroom-login" element={<DataRoomLoginPage />} />
      <Route path="/dataroom-reset-password" element={<DataRoomResetPasswordPage />} />
      <Route path="/dataroom-select" element={<DataRoomSelectorPage />} />
      <Route path="/dataroom-view/:dealId" element={<DataRoomExternalView />} />
      {/* Client Portal routes - no admin auth required */}
      <Route path="/portal/:slug/login" element={<PortalLoginPage />} />
      <Route path="/portal/:slug/reset-password" element={<PortalResetPasswordPage />} />
      <Route path="/portal/:slug" element={<PortalDashboardPage />} />
      <Route path="/portal/:slug/strategic-analysis" element={<PortalStrategicPage />} />
      <Route path="/portal/:slug/company/:companyId" element={<PortalCompanyPage />} />
      <Route path="/portal/:slug/ooh-map" element={<PortalOOHMapPage />} />
      <Route path="/" element={<LandingOrDashboard />} />

      {/* Legal pages — public */}
      <Route path="/legal" element={<LegalNoticePage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/cookies" element={<CookiesPolicyPage />} />
      <Route path="/terms" element={<TermsSaaSPage />} />
      <Route path="/billing-terms" element={<BillingTermsPage />} />
      <Route path="/acceptable-use" element={<AcceptableUsePage />} />
      <Route path="/financial-disclaimer" element={<FinancialDisclaimerPage />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/status" element={<StatusPage />} />
      <Route path="/contact" element={<ContactPage />} />

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
export { API, BACKEND_URL };
